import { Button, Modal, Text, TextArea } from '@shared/components';
import { createContext, useRef, useState } from 'react';

type PromptUserArgs = {
  title: string;
  message: string;
  callback: (value: string) => void;
  placeholder?: string;
};

export type PromptContextType = {
  promptUser: (params: PromptUserArgs) => void;
};

export const PromptContext = createContext<PromptContextType>({
  promptUser: () => {},
});

// NOTE(parlato): alert(), confirm() and prompt() are not available in Outlook and other
// plugin contexts. This provider allows to use a custom modal similar to the native
// window.prompt() function, which can be used in the plugin context.
export const PromptContextProvider = ({
  children,
}: {
  children: JSX.Element;
}) => {
  const [value, setValue] = useState<string>('');
  const [placeholder, setPlaceholder] = useState<string>('');
  const [promptMessage, setPromptMessage] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const callbackRef = useRef<(value: string) => void>(() => {});

  const promptUser = ({
    title,
    message,
    callback,
    placeholder,
  }: PromptUserArgs) => {
    setIsOpen(true);
    setPromptMessage(message);
    setTitle(title);
    setPlaceholder(placeholder || '');
    callbackRef.current = callback;
  };

  const clearAll = () => {
    setValue('');
    setPlaceholder('');
    setPromptMessage('');
    setIsOpen(false);
  };

  return (
    <PromptContext.Provider value={{ promptUser }}>
      {children}
      <Modal.Root open={isOpen}>
        <Modal.Content className="p-3">
          <div className="flex flex-col gap-2">
            <Text className="text-gray-700" type="body-sm" isHeavy>
              {title}
            </Text>
            <Text className="text-gray-700" type="body-xs">
              {promptMessage}
            </Text>
            <TextArea
              size="small"
              value={value}
              onChange={setValue}
              className="my-2"
              placeholder={placeholder}
            />
            <div className="flex justify-end gap-3">
              <Button
                variant="destructive"
                onPress={() => setIsOpen(false)}
                size="small"
              >
                Cancel
              </Button>
              <Button
                className=""
                onPress={() => {
                  callbackRef.current?.(value);
                  clearAll();
                }}
                size="small"
                variant="primary"
              >
                Submit
              </Button>
            </div>
          </div>
        </Modal.Content>
      </Modal.Root>
    </PromptContext.Provider>
  );
};
